import { ChevronRight } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { StyledLinkBlue } from './StyledLink';

const DefaultTooltip = (props) => {
   const { t } = useTranslation();

   const CustomDefaultTooltip = withStyles({
      tooltip: {
         '&.MuiTooltip-tooltip': {
            fontFamily: "'Montserrat', san-serif",
            backgroundColor: '#FFF',
            padding: 0,
            borderRadius: '6px',
            border: '1px solid rgba(0, 0, 0, 0.2)',

            '& .MuiTooltip-arrow': {
               color: '#f7f7f7',

               '&:before': {
                  border: '1px solid rgba(0, 0, 0, 0.25)',
               },
            },
         },
      },
   })(Tooltip);

   return (
      <CustomDefaultTooltip
         title={
            <div className='rounded-md'>
               <div className='px-5 py-3 text-[#6B7280] bg-[#F9FAFB] rounded-t-md text-sm border-b'>{props.title}</div>
               <div className='px-5 py-3 flex flex-col gap-3 text-[#6B7280] text-xs'>
                  {props.content}
                  {props.readMore ? (
                     <StyledLinkBlue to={props.readMoreHref} className='pointer-events-none cursor-not-allowed opacity-50'>
                        <div className='flex items-center'>
                           <p>{t('common.read-more')}</p>
                           <ChevronRight fontSize='small' />
                        </div>
                     </StyledLinkBlue>
                  ) : (
                     <></>
                  )}
               </div>
            </div>
         }
         arrow
      >
         <div>{props.children}</div>
      </CustomDefaultTooltip>
   );
};

/** Làm tròn tối đa 2 chữ số thập phân để hiển thị trong tooltip */
const formatTooltipDays = (value) => {
   const numberValue = Number(value);
   if (!Number.isFinite(numberValue)) return value ?? 0;
   const rounded = Math.round(numberValue * 100) / 100;
   return Number.isInteger(rounded) ? rounded : rounded;
};

const DEFAULT_DAY_SECONDS = 8 * 3600;

/** Parse "HH:mm:ss" → tổng giây */
const parseTimeToSeconds = (timeStr) => {
   if (!timeStr || typeof timeStr !== "string") return 0;
   const parts = timeStr.trim().split(":").map(Number);
   if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return 0;
   const [h = 0, m = 0, s = 0] = parts;
   return h * 3600 + m * 60 + s;
};

/** Setting có giây (HH:mm:ss) hay chỉ giờ-phút (HH:mm) */
const shouldShowSeconds = (timeFormat) =>
   /ss/i.test(String(timeFormat || "HH:mm:ss"));

/**
 * Số giây → "X giờ YY phút ZZ giây"
 * Nếu setting là HH:mm → chỉ "X giờ YY phút"
 */
const formatHmsLabel = (totalSeconds, t, timeFormat) => {
   const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
   const h = Math.floor(secs / 3600);
   const m = Math.floor((secs % 3600) / 60);
   const s = secs % 60;
   const hoursPart = `${h} ${t("common.hours").toLowerCase()}`;
   const minutesPart = `${String(m).padStart(2, "0")} ${t("common.minutes").toLowerCase()}`;

   if (!shouldShowSeconds(timeFormat)) {
      return `${hoursPart} ${minutesPart}`;
   }

   return `${hoursPart} ${minutesPart} ${String(s).padStart(2, "0")} ${t("common.seconds").toLowerCase()}`;
};

/** Số thập phân theo locale (vi → dấu phẩy) */
const formatDecimalForLocale = (value, lang) => {
   const text = String(formatTooltipDays(value));
   return String(lang || "").toLowerCase().startsWith("vi")
      ? text.replace(".", ",")
      : text;
};

/**
 * Ví dụ:
 * - HH:mm:ss → 0,06 ngày (0 ngày 0 giờ 30 phút 00 giây)
 * - HH:mm → 0,06 ngày (0 ngày 0 giờ 30 phút)
 */
const formatDaysWithHmsLabel = (
   days,
   hoursPerLeaveDay,
   numOfTime,
   t,
   lang,
   timeFormat
) => {
   const daySeconds =
      parseTimeToSeconds(hoursPerLeaveDay) > 0
         ? parseTimeToSeconds(hoursPerLeaveDay)
         : DEFAULT_DAY_SECONDS;
   const leaveSeconds = parseTimeToSeconds(numOfTime);
   const wholeDays = Math.floor(leaveSeconds / daySeconds);
   const rem = leaveSeconds % daySeconds;
   const h = Math.floor(rem / 3600);
   const m = Math.floor((rem % 3600) / 60);
   const s = rem % 60;

   const daysDisplay = formatDecimalForLocale(days, lang);
   const hoursPart = `${h} ${t("common.hours").toLowerCase()}`;
   const minutesPart = `${String(m).padStart(2, "0")} ${t("common.minutes").toLowerCase()}`;
   const timePart = shouldShowSeconds(timeFormat)
      ? `${hoursPart} ${minutesPart} ${String(s).padStart(2, "0")} ${t("common.seconds").toLowerCase()}`
      : `${hoursPart} ${minutesPart}`;
   const breakdown = `${wholeDays} ${t("common.day").toLowerCase()} ${timePart}`;

   return `${daysDisplay} ${t("common.day").toLowerCase()} (${breakdown})`;
};

/** HH:mm:ss → nhãn giờ/phút(/giây) theo setting công ty */
const formatTimeValueLabel = (hhMmSs, t, timeFormat) => {
   if (!hhMmSs) return "-";
   return formatHmsLabel(parseTimeToSeconds(hhMmSs), t, timeFormat);
};

/** Cộng HH:mm:ss từ danh sách detail → "HH:mm:ss" */
const sumDetailsToHhMmSs = (details) => {
   const totalSecs = (details || []).reduce(
      (sum, item) =>
         sum +
         parseTimeToSeconds(item.usedTime || item.numOfTime || item.time),
      0
   );
   const h = Math.floor(totalSecs / 3600);
   const m = Math.floor((totalSecs % 3600) / 60);
   const s = totalSecs % 60;
   return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Tooltip số ngày phép:
 * 1) Cách tính (trên)
 * 2) Tổng thời gian đã nghỉ = số ngày xấp xỉ (chi tiết bên dưới nếu nhiều giai đoạn)
 */
export const LeaveUsedDaysTooltipContent = ({ details = [], totalDays }) => {
   const { t, i18n } = useTranslation();
   const { genaralSettingTimeFormat } = useAuth();
   if (!details.length) return null;

   const hasMany = details.length > 1;
   const lang = i18n.language;
   const timeFormat = genaralSettingTimeFormat || "HH:mm:ss";

   const primaryHoursPerDay =
      details[0]?.hoursPerDay || details[0]?.hoursPerLeaveDay;
   const primaryHoursPerDayLabel = formatTimeValueLabel(
      primaryHoursPerDay,
      t,
      timeFormat
   );

   const totalTimeHhMmSs = sumDetailsToHhMmSs(details);
   const totalTimeLabel = formatHmsLabel(
      parseTimeToSeconds(totalTimeHhMmSs),
      t,
      timeFormat
   );
   const totalDaysLabel = formatDaysWithHmsLabel(
      totalDays,
      primaryHoursPerDay,
      totalTimeHhMmSs,
      t,
      lang,
      timeFormat
   );

   return (
      <div className="flex flex-col gap-2">
         {/* Phía trên: giữ mô tả cũ */}
         <p className="text-[#6B7280]">
            {t("common.unpaid-month-single-explain")}
         </p>
         {hasMany ? (
            <p className="text-[#6B7280]">
               {t("common.unpaid-month-rule-changed")}
            </p>
         ) : null}

         {/* Bên dưới: 1 ngày = … → Tính toán: thời gian ÷ 1 ngày = kết quả */}
         <div className="flex flex-col gap-2 border-t border-[#E5E7EB] pt-2">
            <p className="text-[#111827]">
               {t("common.unpaid-month-total-time", {
                  time: totalTimeLabel,
               })}
            </p>
            <p className="text-[#111827]">
               {t("common.unpaid-month-day-rule", {
                  hoursPerLeaveDay: primaryHoursPerDayLabel,
               })}
            </p>
            <p className="font-semibold text-[#111827]">
               {t("common.unpaid-month-calc", {
                  numOfTime: totalTimeLabel,
                  hoursPerLeaveDay: primaryHoursPerDayLabel,
                  days: totalDaysLabel,
               })}
            </p>

            {hasMany ? (
               <div className="flex flex-col gap-2">
                  <p className="font-semibold text-[#111827]">
                     {t("common.unpaid-month-details-title")}
                  </p>
                  {details.map((d, index) => {
                     const time = d.usedTime || d.numOfTime || d.time;
                     const hoursPerDay = d.hoursPerDay || d.hoursPerLeaveDay;
                     const timeLabel = formatTimeValueLabel(time, t, timeFormat);
                     const hoursPerDayLabel = formatTimeValueLabel(
                        hoursPerDay,
                        t,
                        timeFormat
                     );
                     const daysLabel = formatDaysWithHmsLabel(
                        d.days,
                        hoursPerDay,
                        time,
                        t,
                        lang,
                        timeFormat
                     );

                     return (
                        <div
                           key={`${time}-${hoursPerDay}-${index}`}
                           className={index > 0 ? "pt-1" : ""}
                        >
                           <p className="font-medium text-[#374151]">
                              {t("common.unpaid-month-period-title", {
                                 index: index + 1,
                                 hoursPerLeaveDay: hoursPerDayLabel,
                              })}
                           </p>
                           <p className="text-[#4B5563]">
                              {t("common.unpaid-month-detail-eq", {
                                 time: timeLabel,
                                 days: daysLabel,
                              })}
                           </p>
                        </div>
                     );
                  })}
               </div>
            ) : null}
         </div>
      </div>
   );
};

/**
 * Tooltip giờ nghỉ không lương theo tháng:
 * số ngày = tổng thời gian nghỉ không lương ÷ giờ quy định 1 ngày.
 */
export const UnpaidLeaveHoursTooltipContent = ({
   numOfTime,
   hoursPerLeaveDay,
   days,
}) => {
   const { t, i18n } = useTranslation();
   const { genaralSettingTimeFormat } = useAuth();
   const lang = i18n.language;
   const timeFormat = genaralSettingTimeFormat || "HH:mm:ss";
   const hoursLabel = formatTimeValueLabel(hoursPerLeaveDay, t, timeFormat);
   const timeLabel = formatTimeValueLabel(numOfTime, t, timeFormat);
   const daysLabel = formatDaysWithHmsLabel(
      days,
      hoursPerLeaveDay,
      numOfTime,
      t,
      lang,
      timeFormat
   );

   return (
      <div className="flex flex-col gap-2">
         <p className="text-[#6B7280]">{t("common.unpaid-hours-explain")}</p>
         <div className="flex flex-col gap-2 border-t border-[#E5E7EB] pt-2">
            <p className="text-[#111827]">
               {t("common.unpaid-hours-total-time", { time: timeLabel })}
            </p>
            <p className="text-[#111827]">
               {t("common.unpaid-month-day-rule", {
                  hoursPerLeaveDay: hoursLabel,
               })}
            </p>
            <p className="font-semibold text-[#111827]">
               {t("common.unpaid-month-calc", {
                  numOfTime: timeLabel,
                  hoursPerLeaveDay: hoursLabel,
                  days: daysLabel,
               })}
            </p>
         </div>
      </div>
   );
};

export default DefaultTooltip;
